import { FC } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { format } from 'date-fns';
import { CollectionSearchResult } from '@/services/data-sources/search-collection';

interface CollectionSearchResultsProps {
  results: CollectionSearchResult[];
  loading?: boolean;
  error?: string;
  onClose?: () => void;
}

const SearchResultSkeleton = () => (
  <Card className='mb-4'>
    <CardContent className='pt-6'>
      <div className='space-y-2'>
        <div className='flex items-center justify-between'>
          <Skeleton className='h-4 w-24' />
          <Skeleton className='h-5 w-12' />
        </div>
        <Skeleton className='h-4 w-full' />
        <Skeleton className='h-4 w-full' />
        <Skeleton className='h-4 w-3/4' />
      </div>
    </CardContent>
  </Card>
);

export const CollectionSearchResults: FC<CollectionSearchResultsProps> = ({
  results,
  loading = false,
  error,
  onClose,
}) => {
  if (loading) {
    return (
      <div className='space-y-4'>
        <div className='flex items-center justify-between'>
          <h3 className='text-lg font-medium'>Searching...</h3>
          {onClose && (
            <Button
              variant='ghost'
              size='icon'
              onClick={onClose}
              aria-label='Close search results'
            >
              <X className='h-4 w-4' />
            </Button>
          )}
        </div>
        {Array.from({ length: 3 }).map((_, index) => (
          <SearchResultSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className='space-y-4'>
        <div className='flex items-center justify-between'>
          <h3 className='text-lg font-medium'>Search Error</h3>
          {onClose && (
            <Button
              variant='ghost'
              size='icon'
              onClick={onClose}
              aria-label='Close search results'
            >
              <X className='h-4 w-4' />
            </Button>
          )}
        </div>
        <Card className='border-destructive'>
          <CardContent className='pt-6'>
            <div className='text-center text-destructive'>
              <p className='font-medium'>Search Error</p>
              <p className='text-sm mt-1'>{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className='space-y-4'>
        <div className='flex items-center justify-between'>
          <h3 className='text-lg font-medium'>Search Results (0 found)</h3>
          {onClose && (
            <Button
              variant='ghost'
              size='icon'
              onClick={onClose}
              aria-label='Close search results'
            >
              <X className='h-4 w-4' />
            </Button>
          )}
        </div>
        <Card>
          <CardContent className='pt-6'>
            <div className='text-center text-muted-foreground'>
              <p className='font-medium'>No results found</p>
              <p className='text-sm mt-1'>
                Try adjusting your search query or filters
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <h3 className='text-lg font-medium'>
          Search Results ({results.length} found)
        </h3>
        {onClose && (
          <Button
            variant='ghost'
            size='icon'
            onClick={onClose}
            aria-label='Close search results'
          >
            <X className='h-4 w-4' />
          </Button>
        )}
      </div>

      {results.map((result) => {
        const metadata = result.payload.metadata;
        const title =
          (metadata?.title as string) ||
          (metadata?.document_title as string) ||
          result.payload.title ||
          'Untitled';
        const indexedAt = result.payload.index_time
          ? format(new Date(result.payload.index_time), 'MM/dd/yyyy HH:mm')
          : 'Unknown';
        const sourceTimestamp = result.payload.datetime
          ? format(new Date(result.payload.datetime), 'MM/dd/yyyy HH:mm')
          : undefined;
        const url =
          (metadata?.url as string) ||
          (metadata?.source_url as string) ||
          (result.payload as { url?: string }).url;

        return (
          <Card key={result.id} className='hover:shadow-md transition-shadow'>
            <CardContent className='pt-6'>
              <div className='space-y-3'>
                <div className='flex items-start justify-between'>
                  <div className='flex-1'>
                    <div className='space-y-1 mb-3 text-sm'>
                      <div>
                        <span className='font-semibold text-muted-foreground'>
                          Title:
                        </span>{' '}
                        <span className='font-medium text-foreground'>
                          {title}
                        </span>
                      </div>
                      <div className='text-xs text-muted-foreground flex flex-wrap gap-2'>
                        <span>
                          <span className='font-semibold'>Score:</span>{' '}
                          {result.score.toFixed(4)}
                        </span>
                        <span>
                          <span className='font-semibold'>External ID:</span>{' '}
                          {result.payload.external_id || 'Unknown'}
                        </span>
                        {sourceTimestamp && (
                          <span>
                            <span className='font-semibold'>
                              Source Timestamp:
                            </span>{' '}
                            {sourceTimestamp}
                          </span>
                        )}
                        <span>
                          <span className='font-semibold'>Indexed:</span>{' '}
                          {indexedAt}
                        </span>
                      </div>
                      {url && (
                        <div className='text-xs truncate'>
                          <span className='font-semibold text-muted-foreground'>
                            URL:
                          </span>{' '}
                          <a
                            href={url}
                            target='_blank'
                            rel='noreferrer'
                            className='text-primary underline break-all'
                          >
                            {url}
                          </a>
                        </div>
                      )}
                    </div>
                    <p className='text-sm leading-relaxed whitespace-normal'>
                      {result.payload.text}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
